import CategoriesAPI from "~/services/categories";
import TeamsAPI from "~/services/teams";
import useUserStore from "~/stores/user";
import { ORDER, SORT } from "~/constants";
import useBreakpointsLimit from "~/hooks/use-breakpoints-limit";

const useLimit = useBreakpointsLimit({ sm: 6, md: 6, lg: 8, xl: 12, xl2: 12, xl3: 16, xl4: 22, xl5: 16, xl6: 16 });
const categoryMainLinkPath = (linkId: string) => `/category/${linkId}`;
const leagueMainLinkPath = (linkId: string) => `/league/${linkId}`;
const categorySubLinkPath = (linkId: string, mainLinkId?: string) => `${mainLinkId && categoryMainLinkPath(mainLinkId)}/sub-category/${linkId}/`;
const leagueSubLinkPath = (linkId: string) => `/team/${linkId}/`;
const formatSubLink = (id: string, title: string, mainId?: string, imageUrl?: string, href?: string): IHeaderSubLink => {
  const appConfig = useRuntimeConfig().public;
  return {
    id,
    title,
    mainId,
    imageUrl,
    href: href || appConfig.APP_CUSTOMER.enable_leagues_view ? leagueSubLinkPath(id) : categorySubLinkPath(id, mainId),
  };
};
const formatLink = (id: string, title: string, imageUrl?: string, href?: string): IHeaderLink => {
  const appConfig = useRuntimeConfig().public;
  return {
    id,
    title,
    imageUrl,
    href: href || appConfig.APP_CUSTOMER.enable_leagues_view ? leagueMainLinkPath(id) : categoryMainLinkPath(id),
  };
};

// TODO: Replace this mess with a single API call to the BE.
const HeaderAPI = {
  async load(): Promise<IHeaderLink[]> {
    const appConfig = useRuntimeConfig().public;
    const customeHeaderLinks = appConfig.APP_CUSTOMER.header_links as unknown as Link[] || [];

    const cache = useSessionStorage<IHeaderLink[]>(`${appConfig.APP_CUSTOMER.name_short}-links`, []);

    const followingLink = {
      id: "following",
      title: "Following",
      href: "/following/",
      withUserOnly: true,
      subLinks: HeaderAPI.loadSubLinksFollowing(),
    };

    const archivedLink = {
      id: "archive",
      title: "Archive",
      href: "/archive/",
      subLinks: [] as IHeaderLink[],
    };

    const customLinks = customeHeaderLinks.map(link => ({ id: link.title, title: link.title, href: link.url }));

    const hasFollowingLink = appConfig.APP_FLAGS.hasFollowing && !appConfig.APP_CUSTOMER.enable_leagues_view;

    let links: IHeaderLink[];
    if (cache.value && cache.value.length) {
      links = [...cache.value];
    } else {
      if (appConfig.APP_CUSTOMER.enable_archived_league && appConfig.APP_CUSTOMER.enable_leagues_view)
        archivedLink.subLinks = await HeaderAPI.loadArchivedLeague();

      const [categories, subLinks] = await Promise.all([
        CategoriesAPI.load({ page: 0, pageSize: 50, order: ORDER.asc, sort: SORT.order }).then(response => response.content.entries),
        HeaderAPI.loadSubLinks(),
      ]);

      links = categories.map((cat) => {
        const mainLink = formatLink(cat.id, cat.title, "", cat.link);
        const headerSubLinks = HeaderAPI.getSubLinksForCategory(subLinks, cat.id);

        mainLink.subLinks = headerSubLinks.slice(0, useLimit.value);
        mainLink.hasMore = headerSubLinks.length > useLimit.value;

        return mainLink;
      });

      if (customLinks.length > 0)
        links.unshift(...customLinks);

      if (hasFollowingLink)
        links.unshift(followingLink);

      if (archivedLink.subLinks.length > 0)
        links.push(archivedLink);

      cache.value = links;
    }
    return links;
  },

  getSubLinksForCategory(subLinks: IHeaderSubLink[], categoryId: string): IHeaderSubLink[] {
    return subLinks
      .filter(link => categoryId === link.mainId)
      .sort((a, b) => a.title.localeCompare(b.title)) || [];
  },

  loadSubLinks(): Promise<IHeaderSubLink[]> {
    const appConfig = useRuntimeConfig().public;
    if (appConfig.APP_CUSTOMER.enable_leagues_view) {
      return TeamsAPI.load({ pageSize: 700 }).then(response => response.reduce((acc, team) => {
        team.mainCategoryIds.forEach(mainCategoryId => acc.push(formatSubLink(team.id, team.title, mainCategoryId, team.imageUrl)));
        return acc;
      }, [] as IHeaderSubLink[]));
    }

    // TODO: Mapping of the sub categories is too complex so better to have relevant BE for header links.
    return Promise.resolve([]);
    // return CategoriesAPI.subCategories.load({ pageSize: 300 }).then(response => response.content.entries
    //   .map(cat => formatSubLink(cat.id, "mainLinkId", cat.title, cat.imageUrl, cat.link)));
  },

  loadSubLinksFollowing(): IHeaderSubLink[] {
    const userStore = useUserStore();
    return userStore.followedIdentityItems.map((item) => {
      const identitiesId = item.identitiesId && item.identitiesId.length > 0 ? item.identitiesId[0] : undefined;
      return formatSubLink(item.id, item.title, identitiesId, item.imageUrl);
    });
  },

  async loadArchivedLeague(): Promise<IHeaderLink[]> {
    const archivedLeague = await CategoriesAPI.load({
      archived: true,
      page: 0,
      pageSize: 16,
      order: ORDER.asc,
      sort: SORT.order,
    }).then(response => response.content.entries);
    return archivedLeague.map(league => formatLink(league.id, league.title, league.imageUrl, `league/${league.id}`));
  },
};

export default HeaderAPI;
